import { changeMeta, createField } from '@/components/forms/field.schema';
import {
  ChecklistTagsField,
  TemplateTagsField,
} from '@/features/lists/components/ListTagsField';
import { TemplateSelectorField } from '@/features/lists/components/TemplateSelectorField';
import { ListSummary } from '@certhon/models';
import { Megaphone } from 'lucide-react';
import { z } from 'zod';

export const listFields = {
  name: createField(z.string().min(1, 'Name is required'), {
    fieldType: 'text',
    label: 'Name',
    fullWidth: true,
    placeholder: 'List name',
  }),
  // FIXME: changeMeta(name, { visibleIn: ['edit'] }) doesn't work reliably
  // (see ticket.schemas.ts for same issue). Duplicating the field definition
  // as a workaround until changeMeta is fixed.
  nameInEdit: createField(z.string().min(1, 'Name is required'), {
    fieldType: 'text',
    label: 'Name',
    fullWidth: true,
    placeholder: 'List name',
    visibleIn: ['edit'],
  }),
  status: createField(z.enum(['open', 'closed']), {
    fieldType: 'select',
    label: 'Status',
    options: [
      { label: 'Open', value: 'open' },
      { label: 'Completed', value: 'closed' },
    ],
  }),
  werk_id: createField(z.int().nullable(), {
    label: 'Work',
    fieldType: 'foreignKey',
    entity: 'work',
  }),
  associated_users: createField(z.array(z.number()), {
    fieldType: 'foreignKeyArray',
    entity: 'user',
    label: 'Assigned',
  }),
  start_date: createField(z.date().nullable(), {
    fieldType: 'date',
    label: 'Start Date',
  }),
  end_date: createField(z.date().nullable(), {
    fieldType: 'date',
    label: 'End Date',
  }),
  created: createField(z.date(), {
    fieldType: 'date',
    label: 'Created',
    readOnly: true,
  }),
  modified: createField(z.date(), {
    fieldType: 'date',
    label: 'Modified',
    readOnly: true,
  }),
  // Legacy tag format: array of `key:value` strings (e.g. `department:climate`).
  // To be replaced by label-based tagging post-migration.
  tags: createField(z.array(z.string().regex(/^[\w-]+:.+$/)), {
    fieldType: 'custom',
    component: ChecklistTagsField,
    label: 'Tags',
    // Filtering is wired up via per-table `customTokens` (see
    // checklists.table.tsx / templates.table.tsx), but the filter function
    // lives here so both tables share the same array-membership semantics.
    filterConfig: {
      filterFn: (value, token) => {
        const tags = Array.isArray(value) ? (value as string[]) : [];
        const has = tags.includes(String(token.value));
        return token.operation === 'is_not' ? !has : has;
      },
    },
  }),
  // Used only in the create flow; emits an EXPAND_PROTO event after CREATE_LIST.
  // `null` is a valid choice meaning "blank checklist".
  template_id: createField(z.uuid().nullable(), {
    fieldType: 'custom',
    component: TemplateSelectorField,
    label: 'Template',
    placeholder: 'Select a template…',
    fullWidth: true,
  }),
};

/**
 * Template fields. Templates share the same data model as checklists,
 * so we re-use the relevant subset of listFields with template-specific overrides.
 */
export const templateFields = {
  name: listFields.name,
  nameInEdit: listFields.nameInEdit,
  // Templates allow an extra `feedback:true|false` tag in addition to the
  // checklist tag keys.
  tags: changeMeta(listFields.tags, { component: TemplateTagsField }),
  created: listFields.created,
  modified: listFields.modified,
  /**
   * Virtual field exposing the unresolved feedback count for filter/sort.
   * The actual cell rendering is handled by a custom column in
   * `templates.table.tsx` because we need to display two numbers
   * (`unresolved/total`) in a single cell.
   */
  feedback: createField(z.number(), {
    fieldType: 'custom',
    label: 'Feedback',
    icon: Megaphone,
    readOnly: true,
    getVirtualValue: (list: ListSummary) => list.feedback_unresolved_count ?? 0,
  }),
};
